import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { onHealthDataUpdated } from '../lib/healthSync';

const HEALTH_SUMMARY_STORAGE_PREFIX = 'qureo.healthSummary.v1';

const getHealthSummaryStorageKey = () => {
  const userId = (typeof window !== 'undefined' && window.localStorage)
    ? window.localStorage.getItem('userId')
    : null;
  return `${HEALTH_SUMMARY_STORAGE_PREFIX}.${userId || 'guest'}`;
};

const readHealthSummaryCache = () => {
  if (typeof window === 'undefined' || !window.localStorage) return null;

  try {
    const raw = window.localStorage.getItem(getHealthSummaryStorageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.summary || typeof parsed.summary !== 'object') return null;
    return parsed.summary;
  } catch {
    return null;
  }
};

const writeHealthSummaryCache = (summary) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (!summary || typeof summary !== 'object') return;

  try {
    const payload = {
      summary,
      cachedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(getHealthSummaryStorageKey(), JSON.stringify(payload));
  } catch {
    // Ignore cache write failures (private mode, quota, etc.)
  }
};

/**
 * Custom hook for managing metrics
 * Fetches health metrics data from backend
 */
export const useMetrics = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/metrics/dashboard');
      if (response.data.success) {
        setMetrics(response.data.metrics);
        setError(null);
        return response.data.metrics;
      }
    } catch (err) {
      console.error('Error fetching dashboard metrics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchVitalsMetrics = useCallback(async (days = 7) => {
    try {
      const response = await api.get(`/api/metrics/vitals?days=${days}`);
      if (response.data.success) {
        return response.data;
      }
    } catch (err) {
      console.error('Error fetching vitals metrics:', err);
      throw err;
    }
  }, []);

  const fetchMedicationsMetrics = useCallback(async () => {
    try {
      const response = await api.get('/api/metrics/medications');
      if (response.data.success) {
        return response.data;
      }
    } catch (err) {
      console.error('Error fetching medications metrics:', err);
      throw err;
    }
  }, []);

  const fetchAlertsMetrics = useCallback(async () => {
    try {
      const response = await api.get('/api/metrics/alerts');
      if (response.data.success) {
        return response.data;
      }
    } catch (err) {
      console.error('Error fetching alerts metrics:', err);
      throw err;
    }
  }, []);

  const getCachedHealthSummary = useCallback(() => readHealthSummaryCache(), []);

  const fetchHealthSummary = useCallback(async (options = {}) => {
    const { forceRefresh = false } = options;

    if (!forceRefresh) {
      const cached = readHealthSummaryCache();
      if (cached) return cached;
    }

    try {
      const response = await api.get('/api/metrics/summary');
      if (response.data.success) {
        const summary = response.data.summary;
        writeHealthSummaryCache(summary);
        return summary;
      }
    } catch (err) {
      console.error('Error fetching health summary:', err);
      const cached = readHealthSummaryCache();
      if (cached) return cached;
      throw err;
    }
  }, []);

  // Auto-fetch on mount
  useEffect(() => {
    fetchDashboardMetrics();
  }, [fetchDashboardMetrics]);

  // Warm health-summary cache early so score-dependent screens render instantly.
  useEffect(() => {
    fetchHealthSummary().catch(() => {
      // Ignore prefetch failures; individual screens handle fallback behavior.
    });
  }, [fetchHealthSummary]);

  // Keep metrics and cached summary fresh when any screen updates health data.
  useEffect(() => {
    const unsubscribe = onHealthDataUpdated(() => {
      fetchDashboardMetrics();
      fetchHealthSummary({ forceRefresh: true }).catch(() => {
        // Ignore background refresh failures.
      });
    }, { debounceMs: 250 });

    return unsubscribe;
  }, [fetchDashboardMetrics, fetchHealthSummary]);

  return {
    metrics,
    loading,
    error,
    fetchDashboardMetrics,
    fetchVitalsMetrics,
    fetchMedicationsMetrics,
    fetchAlertsMetrics,
    getCachedHealthSummary,
    fetchHealthSummary,
  };
};

export default useMetrics;
